import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useEffect, useRef } from "react";

type ParticipantOption = {
  id: string;
  lead_id: string;
  label: string;
  quantity: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

async function syncPaxCount(leadId: string) {
  const { data: first } = await (supabase as any)
    .from("lead_participant_options")
    .select("quantity")
    .eq("lead_id", leadId)
    .order("sort_order", { ascending: true })
    .limit(1)
    .single();

  if (first) {
    await supabase
      .from("leads")
      .update({ pax_count: first.quantity })
      .eq("id", leadId);
  }
}

export function useParticipantOptions(leadId: string | null) {
  const queryClient = useQueryClient();
  const migrationAttempted = useRef(false);

  const queryKey = ["participant-options", leadId];

  const { data: options = [], isLoading, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!leadId) return [] as ParticipantOption[];
      const { data, error } = await (supabase as any)
        .from("lead_participant_options")
        .select("*")
        .eq("lead_id", leadId)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data || []) as ParticipantOption[];
    },
    enabled: !!leadId,
  });

  // Auto-migrate legacy leads with no options
  useEffect(() => {
    if (!leadId || isLoading || migrationAttempted.current) return;
    if (options.length > 0) return;

    migrationAttempted.current = true;

    (async () => {
      const { data: lead } = await supabase
        .from("leads")
        .select("pax_count")
        .eq("id", leadId)
        .single();

      const qty = lead?.pax_count || 1;

      await (supabase as any).from("lead_participant_options").insert({
        lead_id: leadId,
        label: "Option 1",
        quantity: qty,
        sort_order: 0,
      });

      refetch();
    })();
  }, [leadId, isLoading, options.length, refetch]);

  const addOption = useMutation({
    mutationFn: async () => {
      if (!leadId) return;
      const maxSort = options.length > 0
        ? Math.max(...options.map((o) => o.sort_order))
        : -1;

      const { error } = await (supabase as any).from("lead_participant_options").insert({
        lead_id: leadId,
        label: `Option ${options.length + 1}`,
        quantity: 1,
        sort_order: maxSort + 1,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateOption = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: { label?: string; quantity?: number };
    }) => {
      const { error } = await (supabase as any)
        .from("lead_participant_options")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;

      // If this is the first option by sort_order, sync pax_count
      if (leadId && updates.quantity !== undefined) {
        const firstOption = options[0]; // already sorted by sort_order ASC
        if (firstOption && firstOption.id === id) {
          await supabase
            .from("leads")
            .update({ pax_count: updates.quantity })
            .eq("id", leadId);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      if (leadId) {
        queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
        queryClient.invalidateQueries({ queryKey: ["leads"] });
      }
    },
  });

  const deleteOption = useMutation({
    mutationFn: async (id: string) => {
      if (options.length <= 1) {
        toast.error("At least one participant option is required");
        throw new Error("Cannot delete last option");
      }

      const { error } = await (supabase as any)
        .from("lead_participant_options")
        .delete()
        .eq("id", id);
      if (error) throw error;

      if (leadId) {
        await syncPaxCount(leadId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      if (leadId) {
        queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
        queryClient.invalidateQueries({ queryKey: ["leads"] });
      }
    },
  });

  return {
    options,
    isLoading,
    addOption,
    updateOption,
    deleteOption,
  };
}
